import { useState, useRef, useEffect } from "react"

import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import {
  useMutationDeleteClass,
  useMutationUnarchiveClass,
} from "api/reactQuery/mutations/classes"
import { useQueryArchivedClasses } from "api/reactQuery/queries/classes"
import { ReactComponent as IconArrow } from "assets/common/svg/arrowBack.svg"
import { ReactComponent as IconCheck } from "assets/common/svg/check.svg"
import { ReactComponent as IconCross } from "assets/common/svg/cross.svg"
import { ReactComponent as IconEyeClosed } from "assets/common/svg/eye-closed.svg"
import { ReactComponent as IconEyeOpen } from "assets/common/svg/eye-open.svg"
import { ReactComponent as IconHourglass } from "assets/common/svg/hourglass-outlined.svg"
import { ReactComponent as IconTrash } from "assets/common/svg/trash.svg"
import DynamicIcon from "components/common/icon/dynamicIcon"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppSelector } from "hooks/store"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { selectUser } from "store/auth/auth.selectors"
import { checkSubscription, getErrorMessage } from "utils/api"

import { TriggerStack } from "./HiddenClass.styles"

type ConfirmingAction = "restore" | "delete"

interface IConfirming {
  id: number
  action: ConfirmingAction
}

const HiddenClass = () => {
  const [isOpenHiddenClass, setIsOpenHiddenClass] = useState(false)
  const [confirming, setConfirming] = useState<IConfirming | null>(null)
  const [hoveredId, setHoveredId] = useState<number | null>(null)

  const theme = useTheme()
  const userInfo = useAppSelector(selectUser)
  const modalRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const { showSnackbar } = useCustomSnackbar()
  const { t } = useTranslation()

  const { data } = useQueryArchivedClasses({
    options: {
      enabled: checkSubscription(userInfo),
    },
  })

  const { mutate: unarchiveClass, isLoading: isUnarchivingLoading } =
    useMutationUnarchiveClass({
      options: {
        onSuccess: (_, variables) => {
          queryClient.invalidateQueries("classes")
          queryClient.invalidateQueries("archivedClasses")
          const className =
            data?.data.find((c) => c.id === variables.classId)?.name ?? ""
          showSnackbar({
            title: t("addNewClass.youHaveRestoredClassFromTheArchive", {
              class: className,
            }),
          })
          setConfirming(null)
        },
        onError: (error) => {
          showSnackbar({
            title: getErrorMessage(error),
            variant: "error",
          })
        },
      },
    })

  const { mutate: deleteClass, isLoading: isDeletingLoading } =
    useMutationDeleteClass({
      options: {
        onSuccess: () => {
          queryClient.invalidateQueries("archivedClasses")
          showSnackbar({
            title: t("addNewClass.classHasBeenDeletedSuccessful"),
          })
          setConfirming(null)
        },
        onError: (error) => {
          showSnackbar({
            title: getErrorMessage(error),
            variant: "error",
          })
        },
      },
    })

  const archivedCount = data?.data.length ?? 0

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpenHiddenClass &&
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node)
      ) {
        setIsOpenHiddenClass(false)
        setConfirming(null)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpenHiddenClass])

  return (
    <>
      <TriggerStack
        ref={triggerRef}
        isOpen={isOpenHiddenClass}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        role="button"
        tabIndex={0}
        aria-expanded={isOpenHiddenClass}
        aria-label={t("addNewClass.archivedClassesLabel")}
        onClick={() => {
          setIsOpenHiddenClass(!isOpenHiddenClass)
          setConfirming(null)
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            setIsOpenHiddenClass(!isOpenHiddenClass)
            setConfirming(null)
          }
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          columnGap="8px"
          flex={1}
          minWidth={0}
          sx={{
            color: isOpenHiddenClass
              ? theme.palette.mockup.secondary80
              : theme.palette.mockup.whiteAlpha65,
            transition: "color 0.2s ease",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              opacity: isOpenHiddenClass ? 1 : 0.65,
              transition: "opacity 0.2s ease",
              "& svg": { width: 18, height: 18 },
            }}
          >
            <IconHourglass />
          </Box>

          <Typography
            noWrap
            sx={{
              fontWeight: 500,
              fontSize: "0.8125rem",
              lineHeight: 1.4,
              color: isOpenHiddenClass
                ? theme.palette.mockup.secondary80
                : theme.palette.mockup.whiteAlpha75,
              transition: "color 0.2s ease",
            }}
          >
            {t("addNewClass.archivedClassesLabel")}
          </Typography>

          {archivedCount > 0 && (
            <Box
              sx={{
                flexShrink: 0,
                minWidth: "20px",
                height: "20px",
                px: "6px",
                borderRadius: "10px",
                backgroundColor: isOpenHiddenClass
                  ? theme.palette.mockup.secondary80
                  : theme.palette.mockup.whiteAlpha15,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.25s ease",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  lineHeight: 1,
                  color: isOpenHiddenClass
                    ? theme.palette.mockup.primary60
                    : theme.palette.mockup.whiteAlpha80,
                  transition: "color 0.25s ease",
                }}
              >
                {archivedCount}
              </Typography>
            </Box>
          )}
        </Stack>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            ml: "6px",
            color: isOpenHiddenClass
              ? theme.palette.mockup.secondary80
              : theme.palette.mockup.whiteAlpha40,
            transform: isOpenHiddenClass
              ? "translateX(4px) rotate(180deg)"
              : "translateX(0px) rotate(180deg)",
            transition:
              "transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.2s ease",
            "& svg": { width: 14, height: 14 },
          }}
        >
          <IconArrow />
        </Box>
      </TriggerStack>

      {/* ── Flyout panel ── */}
      <Stack
        ref={modalRef}
        className="smooth-scrollbar"
        sx={{
          zIndex: isOpenHiddenClass ? 10 : -1,
          backgroundColor: theme.palette.mockup.primary60,
          backgroundImage:
            `linear-gradient(150deg, ${theme.palette.mockup.whiteAlpha8} 0%, transparent 55%)`,
          borderLeft: `1.5px solid ${theme.palette.mockup.whiteAlpha10}`,
          boxShadow: isOpenHiddenClass
            ? `6px 0 28px ${theme.palette.mockup.blackAlpha30}, 2px 0 8px ${theme.palette.mockup.blackAlpha20}`
            : "none",
          position: "absolute",
          bottom: 0,
          left: "265px",
          width: "265px",
          px: "10px",
          maxHeight: "500px",
          opacity: isOpenHiddenClass ? 1 : 0,
          transform: isOpenHiddenClass ? "translateX(0)" : "translateX(-14px)",
          transition: isOpenHiddenClass
            ? "opacity 0.25s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease"
            : "opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
          overflowY: "auto",
          pointerEvents: isOpenHiddenClass ? "auto" : "none",
          borderRadius: "0 12px 12px 0",
        }}
      >
        {/* Panel header */}
        <Box
          sx={{
            py: "13px",
            px: "6px",
            borderBottom: `1px solid ${theme.palette.mockup.whiteAlpha10}`,
            mb: "6px",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.6rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: theme.palette.mockup.whiteAlpha40,
              lineHeight: 1,
            }}
          >
            {t("addNewClass.archivedClassesLabel")}
          </Typography>
        </Box>

        <Stack sx={{ pb: "10px" }}>
          {!archivedCount && (
            <Stack alignItems="center" py={3} gap="10px">
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.mockup.whiteAlpha8,
                  border: `1px solid ${theme.palette.mockup.whiteAlpha10}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.palette.mockup.whiteAlpha20,
                  "& svg": { width: 16, height: 16 },
                }}
              >
                <IconHourglass />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  color: theme.palette.mockup.whiteAlpha40,
                  textAlign: "center",
                }}
              >
                {t("addNewClass.noArchivedClasses")}
              </Typography>
            </Stack>
          )}

          {data?.data
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
            )
            .map((classObj, index) => {
              const isConfirming = confirming?.id === classObj.id
              const confirmAction = isConfirming ? confirming?.action : null
              const isHovered = hoveredId === classObj.id
              const isThisUnarchiving =
                isUnarchivingLoading && confirmAction === "restore"
              const isThisDeleting =
                isDeletingLoading && confirmAction === "delete"
              const confirmColors =
                confirmAction === "delete"
                  ? {
                      background: `${theme.palette.mockup.error50}40`,
                      border: `${theme.palette.mockup.error50}80`,
                      hoverBackground: `${theme.palette.mockup.error50}66`,
                      main: theme.palette.mockup.error50,
                    }
                  : {
                      background: theme.palette.mockup.successAlpha25,
                      border: theme.palette.mockup.successAlpha50,
                      hoverBackground: theme.palette.mockup.successAlpha40,
                      main: "#27AE60",
                    }

              return (
                <Stack
                  key={classObj.id}
                  direction="row"
                  alignItems="center"
                  onMouseEnter={() => setHoveredId(classObj.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  sx={{
                    borderRadius: "8px",
                    px: "8px",
                    py: "7px",
                    mb: "2px",
                    minHeight: "38px",
                    cursor: "default",
                    opacity: isOpenHiddenClass ? 1 : 0,
                    transform: isOpenHiddenClass
                      ? "translateX(0)"
                      : "translateX(-10px)",
                    transition: `opacity 0.3s ease ${index * 0.045 + 0.08}s, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.045 + 0.08}s, background-color 0.18s ease`,
                    backgroundColor: isConfirming
                      ? theme.palette.mockup.whiteAlpha10
                      : isHovered
                        ? theme.palette.mockup.whiteAlpha8
                        : "transparent",
                    "&:last-child": { mb: 0 },
                    overflow: "hidden",
                    boxShadow: isConfirming
                      ? `inset 0 0 0 1px ${theme.palette.mockup.whiteAlpha15}`
                      : "none",
                  }}
                >
                  {isConfirming ? (
                    /* ── Inline confirm row ── */
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                      sx={{
                        animation: "fadeSlideIn 0.2s ease forwards",
                        "@keyframes fadeSlideIn": {
                          from: { opacity: 0, transform: "translateX(-6px)" },
                          to: { opacity: 1, transform: "translateX(0)" },
                        },
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: theme.palette.mockup.whiteAlpha80,
                          flex: 1,
                          minWidth: 0,
                          pr: "8px",
                        }}
                      >
                        {confirmAction === "delete"
                          ? t("addNewClass.deleteClassQuestion")
                          : t("addNewClass.restoreClassQuestion")}
                      </Typography>

                      <Stack direction="row" gap="6px" flexShrink={0}>
                        {/* Confirm button */}
                        <Box
                          role="button"
                          tabIndex={0}
                          aria-label={
                            confirmAction === "delete"
                              ? t("addNewClass.confirmDeleteAction")
                              : t("addNewClass.confirmRestoreAction")
                          }
                          onClick={() => {
                            if (isThisDeleting || isThisUnarchiving) return
                            if (confirmAction === "delete") {
                              deleteClass({ classId: classObj.id })
                            } else {
                              unarchiveClass({ classId: classObj.id })
                            }
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== "Enter" && event.key !== " ") {
                              return
                            }
                            event.preventDefault()
                            if (isThisDeleting || isThisUnarchiving) return
                            if (confirmAction === "delete") {
                              deleteClass({ classId: classObj.id })
                            } else {
                              unarchiveClass({ classId: classObj.id })
                            }
                          }}
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: "6px",
                            backgroundColor: confirmColors.background,
                            border: `1px solid ${confirmColors.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: confirmColors.main,
                            transition:
                              "background-color 0.15s ease, transform 0.15s ease",
                            "&:hover": {
                              backgroundColor: confirmColors.hoverBackground,
                              transform: "scale(1.08)",
                            },
                            "&:active": { transform: "scale(0.95)" },
                            "& svg": { width: 12, height: 12 },
                          }}
                        >
                          {isThisUnarchiving || isThisDeleting ? (
                            <CircularProgress
                              size={10}
                              sx={{ color: confirmColors.main }}
                            />
                          ) : (
                            <IconCheck />
                          )}
                        </Box>

                        {/* Cancel button */}
                        <Box
                          role="button"
                          tabIndex={0}
                          aria-label={t("addNewClass.cancelAction")}
                          onClick={() => setConfirming(null)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault()
                              setConfirming(null)
                            }
                          }}
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: "6px",
                            backgroundColor: theme.palette.mockup.whiteAlpha8,
                            border: `1px solid ${theme.palette.mockup.whiteAlpha15}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: theme.palette.mockup.whiteAlpha50,
                            transition:
                              "background-color 0.15s ease, transform 0.15s ease",
                            "&:hover": {
                              backgroundColor: theme.palette.mockup.whiteAlpha15,
                              transform: "scale(1.08)",
                            },
                            "&:active": { transform: "scale(0.95)" },
                            "& svg": { width: 10, height: 10 },
                          }}
                        >
                          <IconCross />
                        </Box>
                      </Stack>
                    </Stack>
                  ) : (
                    /* ── Normal row ── */
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        flex={1}
                        minWidth={0}
                      >
                        <Box
                          sx={{
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            opacity: 0.55,
                            color: theme.palette.common.white,
                          }}
                        >
                          <DynamicIcon
                            name={classObj.icon}
                            variant="outlined"
                          />
                        </Box>
                        <Typography
                          noWrap
                          sx={{
                            py: "2px",
                            px: "9px",
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            color: theme.palette.mockup.whiteAlpha65,
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {classObj.name}
                        </Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" gap="4px">
                        {/* Restore icon — eye toggles on hover */}
                        <Box
                          role="button"
                          tabIndex={0}
                          title={t("addNewClass.restoreClassTooltip")}
                          aria-label={t("addNewClass.restoreClassTooltip")}
                          onClick={() =>
                            setConfirming({
                              id: classObj.id,
                              action: "restore",
                            })
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault()
                              setConfirming({
                                id: classObj.id,
                                action: "restore",
                              })
                            }
                          }}
                          sx={{
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            position: "relative",
                            color: isHovered
                              ? theme.palette.mockup.secondary80
                              : theme.palette.mockup.whiteAlpha40,
                            transition: "color 0.2s ease, transform 0.2s ease",
                            transform: isHovered ? "scale(1.15)" : "scale(1)",
                            "&:active": { transform: "scale(0.95)" },
                            "& svg": { width: 18, height: 18, minWidth: 18 },
                          }}
                        >
                          {isHovered ? <IconEyeOpen /> : <IconEyeClosed />}
                        </Box>

                        {/* Delete icon — only for classes safe to delete */}
                        {classObj.can_be_deleted && (
                          <Box
                            role="button"
                            tabIndex={0}
                            title={t("addNewClass.deleteClassTooltip")}
                            aria-label={t("addNewClass.deleteClassTooltip")}
                            onClick={() =>
                              setConfirming({
                                id: classObj.id,
                                action: "delete",
                              })
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault()
                                setConfirming({
                                  id: classObj.id,
                                  action: "delete",
                                })
                              }
                            }}
                            sx={{
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              position: "relative",
                              color: theme.palette.mockup.whiteAlpha40,
                              transition:
                                "color 0.2s ease, transform 0.2s ease",
                              "&:hover": {
                                color: theme.palette.mockup.error50,
                                transform: "scale(1.15)",
                              },
                              "&:active": { transform: "scale(0.95)" },
                              "& svg": { width: 16, height: 16, minWidth: 16 },
                            }}
                          >
                            <IconTrash />
                          </Box>
                        )}
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              )
            })}
        </Stack>
      </Stack>
    </>
  )
}

export default HiddenClass
